import React, { useEffect, useState } from "react";
import { MapPin, Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning, Loader2 } from "lucide-react";
import { ui, LANG } from "@/data/site";

// Milos / Trypiti coordinates
const LAT = 36.7469;
const LON = 24.4361;
const TZ = "Europe/Athens";

// WMO weather code -> { label, Icon }
function describe(code) {
  const w = ui.weather;
  if (code === 0) return { label: w.clear, Icon: Sun };
  if (code === 1 || code === 2) return { label: w.partly, Icon: CloudSun };
  if (code === 3) return { label: w.overcast, Icon: Cloud };
  if (code === 45 || code === 48) return { label: w.fog, Icon: CloudFog };
  if (code >= 51 && code <= 57) return { label: w.drizzle, Icon: CloudDrizzle };
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return { label: w.rain, Icon: CloudRain };
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return { label: w.snow, Icon: CloudSnow };
  if (code >= 95) return { label: w.storm, Icon: CloudLightning };
  return { label: w.clear, Icon: Sun };
}

export default function LocalConditions() {
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let active = true;
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&current=temperature_2m,weather_code&timezone=${TZ}`)
      .then((r) => r.json())
      .then((d) => { if (active) setWeather(d.current); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const dateStr = new Intl.DateTimeFormat(LANG === "el" ? "el-GR" : "en-GB", { timeZone: TZ, weekday: "long", day: "numeric", month: "long" }).format(now);
  const timeStr = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(now);

  const cond = weather ? describe(weather.weather_code) : null;
  const Dot = () => <span className="h-1 w-1 rounded-full bg-golden/70" aria-hidden="true" />;

  return (
    <div
      className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-ink/10 pb-10 text-ink/60"
      data-testid="footer-local-conditions"
    >
      <span className="inline-flex items-center gap-2 font-medium text-ink">
        <MapPin className="h-4 w-4 text-golden" /> {ui.ctaEyebrow}
      </span>
      <Dot />
      <span>{dateStr}</span>
      <Dot />
      <span className="tabular-nums tracking-wide" data-testid="footer-local-time">{timeStr} <span className="text-ink/40">{ui.localTime}</span></span>
      <Dot />
      {cond ? (
        <span className="inline-flex items-center gap-2" data-testid="footer-weather">
          <cond.Icon className="h-4 w-4 text-golden" />
          {cond.label}
          <span className="ml-1 font-semibold text-ink">{Math.round(weather.temperature_2m)}&deg;C</span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-2 text-ink/40">
          <Loader2 className="h-4 w-4 animate-spin" /> {ui.fetchingWeather}
        </span>
      )}
    </div>
  );
}
